import {
	createSystemConnection,
	resetSystemConnectionTestStatus,
} from '@Actions/systemConnections';
import {
	NewSystemConnectionForm,
	NewSystemConnectionFormConfigurations,
} from '@Components';
import {
	Button,
	decoratedAttributes,
	InfoCard,
	WarningDialog,
} from '@innovyze/stylovyze';
import { BodyGridStyled } from '@Pages/shared/Pages.styles';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resetSystemConnectionStore } from '@Actions';
import { TestCard } from '@Components/TestCard';
import { useYupSchemaValidation } from '@Hooks';
import { SystemConnectionSchema } from '@Pages/shared/SystemConnectionSchema';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import {
	DateFormatOptions,
	QuoteFormatOptions,
	TimestampConversionOptions,
	TagFieldFormatOptions,
	SystemConnection,
} from '@Types/systemConnections.types';
import { useHistory } from 'react-router-dom';
import PageWrapperStyled from '@Pages/CreateValuemapping/CreateValuemappingProfile.styles';
import { StyledControlsWrapper } from '@Pages/EditSensor/EditSensor.styles';
import {
	SECURITY_MODES_DEFAULT,
	SECURITY_POLICIES_DEFAULT,
} from '@Components/NewSystemConnectionForm/OpcuaTypes';
import { useConnectionString } from '../../hooks/useConnectionString';

// prettier-ignore
const CreateSystemConnection = () => {//NOSONAR
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const {
		systemConnections: { status: asyncStatus, testSystemConnectionStatus },
	} = useSelector(getValue);

	const [formData, setFormData] = React.useState<Record<string, string>>({
		serverType: NewSystemConnectionFormConfigurations.serverTypeOptions[0],
		connectionType: NewSystemConnectionFormConfigurations.connectionTypeOptions[0],
		connectionString: NewSystemConnectionFormConfigurations.connectionString,
		databaseTimezone: 'UTC',
		sampleDurationSeconds: '300',
		queryLagMinutes: '30',
		queryGroupSize: '1000',
		caseSensitiveSensorId: false.toString(),
		simulationSubtractionDays: '0',
		timeout: '30',
		port: '1433', // default MSSQL port
		dateTimeFormat: DateFormatOptions.ANSI.value,
		quoteFormat: QuoteFormatOptions.DOUBLE_QUOTE.value,
		timestampConversion: TimestampConversionOptions.IMPLICIT.value,
		tagFormat: TagFieldFormatOptions.STRING.value,
		editMode: 'false',
		securityMode: SECURITY_MODES_DEFAULT,
		securityPolicy: SECURITY_POLICIES_DEFAULT,
		endpointMustExist: 'false',
		sessionName: '',
		queryPrefix: '',
	});
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
		{},
	);
	const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

	const [validate] = useYupSchemaValidation<
		Record<string, string | number>
	>();

	const newSysConnStrHookRet = useConnectionString(
		formData as unknown as SystemConnection,
	);

	useEffect(() => {
		updateFormData({
			connectionString: newSysConnStrHookRet.connectionString,
		});
	}, [newSysConnStrHookRet.connectionString]);

	const testCredentials = React.useMemo(
		() => ({
			username: formData.username,
			password: formData.password,
			connectionString: formData.connectionString,
			type: formData.connectionType,
		}),
		[formData],
	);

	const testCredentialsOPCUA = React.useMemo(
		() => ({
			username: formData.username,
			password: formData.password,
			opcuaUrl: formData.opcuaUrl,
			endpointMustExist: formData.endpointMustExist,
			securityMode: formData.securityMode,
			securityPolicy: formData.securityPolicy,
			sessionName: formData.sessionName || `adsk-edge-client-test-${Date.now()}`,
		}),
		[formData],
	);

	function updateFormData(newFormData: Record<string, string>): void {
		setFormData((currentFormData: Record<string, string>) => ({
			...currentFormData,
			...newFormData,
		}));
	}

	function validateFormData(_formData: Record<string, string | number>) {
		const validationResults = validate(
			_formData,
			SystemConnectionSchema(t),
		);

		if (validationResults.hasErrors) {
			setFormErrors(validationResults.errors as Record<string, string>);
			return false;
		}
		setFormErrors({});
		setFormData(validationResults.result as Record<string, string>);
		return true;
	}

	function handleCreateSystemConnectionClick() {
		if (formData.connectionType && formData.connectionType === 'OPC-UA') {
			delete formData.caseSensitiveSensorId;
			delete formData.connectionString;
			delete formData.dateTimeFormat;
			delete formData.quoteFormat;
			delete formData.serverType;
			delete formData.editMode;
			delete formData.undefined;
			delete formData.queryPrefix;
			if (validateFormData(formData)) {
				dispatch(
					createSystemConnection({
						...formData,
					}),
				);
			}
		}

		if (formData.connectionType && (formData.connectionType === 'ODBC' || formData.connectionType === 'OLEDB')) {
			delete formData.editMode;
			delete formData.opcuaUrl;
			delete formData.resourcePath;
			delete formData.undefined;
			delete formData.securityMode;
			delete formData.securityPolicy;
			delete formData.endpointMustExist;
			delete formData.sessionName;
			if (validateFormData(formData)) {
				dispatch(
					createSystemConnection({
						...formData,
					}),
				);
			}
		}
	}

	const handleCancelClick = () => {
		history.goBack();
	};

	const handleClickOpenConfirmDialog = () => {
		setConfirmDialogOpen(true);
	};

	const handleCloseConfirmDialog = () => {
		setConfirmDialogOpen(false);
	};

	const isFormPopulated = () => {
		for (const formDataKey in formData) {
			if (
				formData[formDataKey] != null &&
				formData[formDataKey] != undefined
			) {
				return true;
			}
		}
		return false;
	};

	const { type: connectionType } = formData;
	const adpAttrs = decoratedAttributes({
		connectionType
	});
	const handleBackBtnClick = isFormPopulated() ? handleClickOpenConfirmDialog : handleCancelClick;

	useEffect(() => {
		if (asyncStatus === 'completed') {
			dispatch(resetSystemConnectionStore());
			history.push(MAIN_ROUTES.SystemConnections);
		}
	}, [asyncStatus]);

	useEffect(() => {
		dispatch(resetSystemConnectionTestStatus());
	}, []);

	const renderForm = (credentials: Record<string, string>) => {
		return (
			<>
				<PageWrapperStyled
					title={t('New System Connection')}
					back={{
						action: handleBackBtnClick,
						label: t('All system connections'),
					}}
					secondary={
						<StyledControlsWrapper>
							<Button
								adp-click="true"
								id="create-system-connection"
								type="button"
								name="Create System Connection"
								dataCy="SaveSystemConnectionButton"
								variant="contained"
								color="primary"
								onClick={handleCreateSystemConnectionClick}
								disabled={asyncStatus === 'pending'}
								{...adpAttrs}>
									{t('Create System Connection')}
							</Button>
						</StyledControlsWrapper>
					}
					applyPadding
					headerUnderline>
					<BodyGridStyled container>
						<InfoCard fullHeight title={t('Details')}>
							<NewSystemConnectionForm
								updateFormData={updateFormData}
								formData={formData}
								formErrors={formErrors}
							/>
						</InfoCard>
						<InfoCard fullHeight title={t('Status')}>
							<TestCard
								credentials={credentials}
								status={testSystemConnectionStatus}
								buttonText={t('Test Connection')}
								isLoading={asyncStatus === 'pending'}
							/>
						</InfoCard>
					</BodyGridStyled>
				</PageWrapperStyled>

				<WarningDialog
						onCancel={handleCloseConfirmDialog}
						open={confirmDialogOpen}
						cancelText={t('Cancel')}
						confirmText={t('Confirm')}
						title={t('Leave without saving?')}
						onConfirm={handleCancelClick}>
						{t(
							'Are you sure you want to leave? The system connection is unsaved',
						)}
					</WarningDialog>
			</>
		);
	};

	switch (formData.connectionType) {
		case 'ODBC':
			return renderForm(testCredentials);
		case 'OPC-UA':
			return renderForm(testCredentialsOPCUA);
		default:
			return renderForm(testCredentials);
	}
};

export default CreateSystemConnection;
